import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';

@Component({
  selector: 'lib-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ButtonComponent {
  @Input() type: 'Primary' | 'Secondary' | 'Tertiary' = 'Primary';
  @Input() size: 'Small' | 'Medium' | 'Large' = 'Medium';
  @Input() disabled: boolean = false;
  @Input() label: string = '';
  @Input() content: 'Text + Icon' | 'Icon + Text' | 'Text' | 'Icon' = 'Text';
  @Input() ariaLabel: string = '';
  @Input() icon?: string | TemplateRef<any>;
  @Input() danger: boolean = false;
  @Input() width?: string;
  @Input() widthFull: boolean = false;
  @Input() customColor?: string;
  @Output() onClick = new EventEmitter<Event>();

  @HostBinding('style.width') get hostWidth(): string | null {
    return this.widthFull ? '100%' : null;
  }

  @HostBinding('style.display') get hostDisplay(): string | null {
    return this.widthFull ? 'block' : null;
  }

  @HostBinding('style.--custom-btn-bg') get customBgColor(): string | null {
    return this.customColor || null;
  }

  @HostBinding('style.--custom-btn-hover') get customHoverColor(): string | null {
    return this.customColor ? this.darkenColor(this.customColor, 10) : null;
  }

  private darkenColor(hex: string, percent: number): string {
    hex = hex.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Darken
    const factor = (100 - percent) / 100;
    const newR = Math.round(r * factor);
    const newG = Math.round(g * factor);
    const newB = Math.round(b * factor);

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  get iconTemplate(): TemplateRef<any> | null {
    return this.icon instanceof TemplateRef ? this.icon : null;
  }

  protected get classes(): string[] {
    const classList = ['btn'];

    classList.push(`btn--${this.type.toLowerCase()}`);

    classList.push(`btn--${this.size.toLowerCase()}`);

    if (this.content === 'Icon') {
      classList.push('btn--icon-only');
    } else if (this.content === 'Icon + Text') {
      classList.push('btn--icon-text');
    } else if (this.content === 'Text + Icon') {
      classList.push('btn--text-icon');
    }

    if (this.disabled) {
      classList.push('is-disabled');
    }

    if (this.danger) {
      classList.push('btn--danger');
      classList.push(`btn--${this.type.toLowerCase()}-danger`);
    }

    if (this.widthFull) {
      classList.push('btn--width-full');
    }

    if (this.customColor) {
      classList.push('btn--custom-color');
    }

    return classList;
  }
}
